import {useCallback} from 'react';
import {useTypedAppSelector} from '../redux/hooks';
import {useAppDispatch} from '../redux/store';
import {userEndpoints} from '../services/user/slice';

export const useProfile = () => {
  const profile = useTypedAppSelector(state => state.user.profile);
  const isProfileLoading = useTypedAppSelector(state => state.user.isProfileLoading);
  const appDispatch = useAppDispatch();
  const loadProfile = useCallback(async () => {
    try {
      await appDispatch(userEndpoints.getProfile()).unwrap();
    } catch (e) {
      console.error(e);
    }
  }, [appDispatch]);

  /**
   * Check if the user has any of the required permissions
   * @param required - Array of required permission codes. If empty or null, returns true.
   * @param campaignPermissions - Optional array of campaign-specific permission codes
   * @returns boolean indicating if the user has any of the required permissions
   */
  const hasAnyPermission = useCallback(
    (
      required: string[] | ReadonlyArray<string>,
      campaignPermissions: string[] | ReadonlyArray<string> | null | undefined,
    ) => {
      // Super user has all permissions
      if (profile?.is_superuser) return true;
      if (!required || required.length === 0) return true;
      // If campaignPermissions is provided use campaignPermissions only
      if (campaignPermissions !== null && campaignPermissions !== undefined) {
        return campaignPermissions.some(value => required.includes(value));
      }
      // Otherwise, use profile permissions
      return (profile?.permissions || []).some(value => required.includes(value));
    },
    [profile?.is_superuser, profile?.permissions],
  );

  const hasAnyPermissions = useCallback(
    (
      required: Record<string, string[] | ReadonlyArray<string>>,
      campaignPermissions: string[] | ReadonlyArray<string> | null | undefined,
    ): Record<string, boolean> => {
      return Object.keys(required).reduce((out, key) => {
        out[key] = hasAnyPermission(required[key], campaignPermissions);
        return out;
      }, {});
    },
    [hasAnyPermission],
  );
  return {profile, isProfileLoading, hasAnyPermission, hasAnyPermissions, loadProfile};
};
